import type { Identifier } from "dnd-core";
import type { FC } from "react";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import Image from "next/image";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import { classNames } from "../utils/formatters";

export interface CardProps {
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  disabled: boolean;
  removeFile: (index: number, image_url: string) => void;
  url: string;
}

interface DragItem {
  index: number;
  type: string;
}

export const Card: FC<CardProps> = ({
  disabled,
  removeFile,
  index,
  moveCard,
  url,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: "card",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },

    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      // const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      // const hoverMiddleY =
      //   (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      // const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      // const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      //  perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!

      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: "card",
    item: () => {
      return { id: url, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0.7 : 1;
  drag(drop(ref));
  return (
    <div
      className={classNames(
        "group  relative cursor-pointer",
        index === 0 ? "ring-2 ring-orange-500 rounded-md ring-offset-2" : ""
      )}
      ref={ref}
      data-handler-id={handlerId}
      style={{ opacity }}
    >
      {/* {isLoading && index == urls.length - 1 ? (
        <div className="absolute flex w-full h-full items-center justify-center z-10 bg-opacity-50 bg-gray-200 rounded-md">
          <Spinner />
        </div>
      ) : null} */}
      <XMarkIcon
        className="absolute text-gray-500 h-5 w-5 p-0.5 right-1 top-1 z-20 shadow-md rounded-full bg-white opacity-80 hover:opacity-100"
        onClick={() => (!disabled ? removeFile(index, url) : null)}
      />
      <div className="aspect-w-1 aspect-h-1 rounded-md overflow-hidden ring-1 ring-gray-100/50">
        <Image
          src={url}
          alt={`Upload ${index + 1}`}
          fill
          style={{
            objectFit: "cover",
          }}
          className="w-full h-full object-center object-cover hover:scale-[102%] transition-transform duration-200"
        />
      </div>
    </div>
  );
};
